/**
 * Cómo se ve la marca en un mail, en un chat de WhatsApp y en la portada del reporte (DISENO, Branding.dc.html). Los
 * usan la vista previa de Branding y la de Mensajes. Son dibujos de la pantalla, no el mail que sale: ése lo arma el
 * envío con el mismo branding.
 *
 * Lo que va arriba del color de la marca (el nombre, el botón) va en blanco o en negro, el que se lea mejor. El logo va
 * sobre blanco, para que uno oscuro no desaparezca arriba de un color oscuro.
 */

import type { ReactNode } from "react";
import { num } from "../../campana-nueva/utils/format";
import { cssDeLetra, nombreParaMostrar, textoSobre } from "../logic/branding";
import type { Letra, UltimaCampana } from "../types";

export interface EstiloDeMarca {
  color: string;
  letra: Letra;
  logo: string | null;
  /** el nombre de la marca; "" si no se sabe */
  marca: string;
}

/** El logo sobre blanco, o el nombre de la marca en letras grandes. También va en la portada del reporte de Grupos. */
export function MarcaEnLaCabecera({ estilo, alto }: { estilo: EstiloDeMarca; alto: string }) {
  if (estilo.logo) {
    return (
      <span className="inline-flex items-center rounded-lg bg-white px-2.5 py-1.5">
        <img src={estilo.logo} alt={`Logo de ${nombreParaMostrar(estilo.marca)}`} className={`${alto} max-w-[150px] object-contain`} />
      </span>
    );
  }
  return <span className="font-bold uppercase tracking-[0.18em]">{nombreParaMostrar(estilo.marca)}</span>;
}

export function VistaMail({
  estilo,
  asunto,
  cuerpo,
  codigo,
  firma,
  boton = "Pedir ahora",
  pie,
  extra,
}: {
  estilo: EstiloDeMarca;
  asunto: string;
  cuerpo: ReactNode;
  /** null = sin cupón */
  codigo: ReactNode | null;
  firma: string;
  /** null = sin botón (la marca no cargó su web) */
  boton?: string | null;
  /** el pie de baja, al final del mail */
  pie?: string;
  /** debajo del mail, fuera del dibujo (a dónde lleva el botón, por ejemplo) */
  extra?: ReactNode;
}) {
  const texto = textoSobre(estilo.color);
  return (
    <div className="space-y-2">
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" style={{ fontFamily: cssDeLetra(estilo.letra) }} data-testid="vista-mail">
        <div className="px-5 py-4" style={{ background: estilo.color, color: texto }}>
          <MarcaEnLaCabecera estilo={estilo} alto="h-8" />
        </div>
        <div className="flex flex-col gap-3.5 p-5">
          <p className="text-xl font-bold text-slate-900">{asunto}</p>
          <div className="whitespace-pre-line text-[15px] leading-relaxed text-slate-800">{cuerpo}</div>
          {codigo !== null && (
            <span className="self-start rounded-lg border-[1.5px] border-dashed px-4 py-2.5 font-mono text-base text-slate-900" style={{ borderColor: estilo.color }} data-testid="codigo-cupon">
              {codigo}
            </span>
          )}
          {boton !== null && (
            <span className="inline-flex min-h-[44px] items-center self-start rounded-lg px-5 font-semibold" style={{ background: estilo.color, color: texto }}>
              {boton}
            </span>
          )}
          {firma && <p className="text-[15px] text-slate-800">{firma}</p>}
          {pie && <p className="border-t border-slate-100 pt-2 text-xs text-slate-500">{pie}</p>}
        </div>
      </div>
      {extra}
    </div>
  );
}

export function VistaWhatsApp({
  estilo,
  cuerpo,
  codigo,
  firma,
  pie,
  hora = "18:30",
}: {
  estilo: EstiloDeMarca;
  cuerpo: ReactNode;
  codigo: ReactNode | null;
  firma: string;
  /** el pie de baja, al final del mensaje */
  pie?: string;
  hora?: string;
}) {
  const nombre = nombreParaMostrar(estilo.marca);
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200" data-testid="vista-whatsapp">
      <div className="flex items-center gap-2.5 px-4 py-3" style={{ background: estilo.color, color: textoSobre(estilo.color) }}>
        <span className="flex h-8 w-8 flex-none items-center justify-center overflow-hidden rounded-full bg-white text-xs font-bold text-slate-900">
          {estilo.logo ? <img src={estilo.logo} alt="" className="h-full w-full object-contain" /> : nombre.charAt(0).toUpperCase()}
        </span>
        <span className="font-semibold">{nombre}</span>
      </div>
      <div className="bg-[#EFEAE2] px-3.5 py-4">
        <div className="flex max-w-[88%] flex-col gap-2 rounded-[4px_12px_12px_12px] bg-white px-3 py-2.5">
          <p className="whitespace-pre-line text-[15px] leading-normal text-slate-900">
            {cuerpo}
            {codigo !== null && (
              <>
                {" "}
                Código: <strong>{codigo}</strong>
              </>
            )}
          </p>
          {firma && <p className="text-sm text-slate-900">{firma}</p>}
          {pie && <p className="text-sm text-slate-600">{pie}</p>}
          <span className="self-end text-[11px] text-slate-500">{hora}</span>
        </div>
      </div>
    </div>
  );
}

export function VistaReporte({ estilo, campana }: { estilo: EstiloDeMarca; campana: UltimaCampana | null }) {
  const dato = (k: string, v: string) => (
    <div className="flex flex-col gap-0.5 rounded-xl bg-[#F7F6FB] p-3">
      <span className="text-[13px] text-slate-600">{k}</span>
      <strong className="text-lg text-slate-900">{v}</strong>
    </div>
  );
  return (
    <div className="space-y-2">
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" style={{ fontFamily: cssDeLetra(estilo.letra) }} data-testid="vista-reporte">
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4" style={{ background: estilo.color, color: textoSobre(estilo.color) }}>
          <MarcaEnLaCabecera estilo={estilo} alto="h-7" />
          <span className="font-mono text-xs uppercase tracking-[0.08em]">Reporte de clientes</span>
        </div>
        <div className="flex flex-col gap-4 p-5">
          <p className="text-[22px] font-bold text-slate-900">
            {campana ? `Tus ${num(campana.compraron)} clientes, en ${num(campana.grupos)} ${campana.grupos === 1 ? "grupo" : "grupos"}` : "Tus clientes, en grupos"}
          </p>
          <div className="grid grid-cols-2 gap-2.5">
            {dato("Pedidos", campana ? num(campana.pedidos) : "—")}
            {dato("Personas", campana ? num(campana.personas) : "—")}
          </div>
        </div>
      </div>
      <p className="text-sm text-slate-500">{campana ? `Con los números de «${campana.nombre}».` : "Cuando armes la primera campaña, acá van a estar sus números."}</p>
    </div>
  );
}
